import { useEffect, useRef, useState } from "react"
import { Button, Input, Spin } from "antd"
import { CopyFilled, CopyOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { Editor } from "@tinymce/tinymce-react"
import Modal from "src/components/Common/Modal"
import Toast from "src/components/Common/Toast"
import { deletePositionEmailTemplate, setPositionEmailTemplate, updatePositionEmailTemplate } from "src/store/Positions/asyncActions"
import { resetEmailTemplate, setEmailTemplateBody, setEmailTemplateSubject } from "src/store/Positions/reducer"
import { POSITION_EMAIL_TEMPLATE_NAMES } from "../constants"
import ConfirmationModal from "src/components/Common/ConfirmationModal"
import { copyTextToClipboard } from "src/utils/helperFunctions"
import "./EmailTemplate.scss"

const EmailTemplate = ({ }) => {
    const [submitting, setSubmitting] = useState(false)
    const [isDeletionConfirmationVisible, setIsDeletionConfirmationVisible] = useState(false)
    const editorRef = useRef(null)
    const {
        loading,
        body,
        subject,
        variables,
        templateName,
        templateId,
        positionId,
        positionName, } = useSelector((state) => state.positions.emailTemplate)
    const dispatch = useDispatch()

    useEffect(() => {
        if (positionId && templateName) {
            dispatch(setPositionEmailTemplate({ templateName, positionId }))
        }
    }, [positionId, templateName])

    const handleSaveTemplate = async () => {
        try {
            setSubmitting(true)
            const editorContent = editorRef.current?.getContent({ format: "text" })?.trim()
            if (!editorContent) {
                setIsDeletionConfirmationVisible(true)
            } else {
                if (variables?.length) {
                    let regex = /\{(.*?)\}/gi
                    const matchedVariables = [
                        ...editorContent.matchAll(regex).map(match => match[1]),
                        ...(subject ?? "").matchAll(regex).map(match => match[1]),
                    ]
                    let extraVariables = []
                    matchedVariables.forEach(matchedVar => {
                        if (!variables.includes(matchedVar)) {
                            extraVariables.push(matchedVar)
                        }
                    })
                    if (extraVariables.length) {
                        throw new Error(`${extraVariables.join(", ")} ${extraVariables.length > 1 ? "are" : "is"} not included in the variables.`)
                    }
                }
                await dispatch(updatePositionEmailTemplate({ templateName, positionId, body, subject }))
            }
        } catch (err) {
            console.log(err)
            Toast('error', err.message, "Oops!")
        } finally {
            setSubmitting(false)
        }
    }

    const handleDeleteTemplate = async (isConfirmed) => {
        setIsDeletionConfirmationVisible(false)
        if (isConfirmed) {
            await dispatch(deletePositionEmailTemplate({ templateId }))
        }
    }

    const handleCloseModal = () => {
        dispatch(resetEmailTemplate())
    }

    return (
        <Modal
            width="60vw"
            centered
            title={<div>
                <h2>{POSITION_EMAIL_TEMPLATE_NAMES[templateName]} Email Template</h2>
                <h5 className="mt-4">{positionName}</h5>
            </div>}
            destroyOnClose
            cancelModal={handleCloseModal}
            modalVisible={positionId}
            onModalClose={handleCloseModal}
            className="email-template-modal"
        >
            {
                loading
                    ? <>
                        <div className="flex items-center justify-center mt-40">
                            <Spin />
                        </div>
                    </>
                    : <>
                        <p className="text-14 weight-500 mb-8">Email Subject</p>
                        <Input
                            className="mb-16"
                            value={subject}
                            placeholder="Enter email subject"
                            onChange={(e) => dispatch(setEmailTemplateSubject(e.target.value))}
                        />
                        <div className="flex items-center flex-wrap mb-12 variables-container">
                            {
                                variables?.length
                                    ? variables.map(variable => {
                                        return (
                                            <span
                                                key={variable}
                                                className="b-20 flex items-center mr-8 mb-8 py-4 px-12 variable-tag"
                                            >
                                                <p className="text-14 mr-4 opacity-8">{`{${variable}}`}</p>
                                                <CopyOutlined
                                                    style={{ color: "#f4bc77" }}
                                                    className="cursor-pointer text-14 p-4"
                                                    onClick={() => {
                                                        copyTextToClipboard(`{${variable}}`)
                                                        Toast("success", "Variable Copied Successfully.", "Variable copied")
                                                    }}
                                                />
                                            </span>
                                        )
                                    })
                                    : <></>
                            }
                        </div>
                        <Editor
                            onInit={(evt, editor) => (editorRef.current = editor)}
                            tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                            id={"template-input"}
                            value={body}
                            onEditorChange={(content, editor) => {
                                dispatch(setEmailTemplateBody(content))
                            }}
                            init={{
                                min_height: 500,
                                max_height: 500,
                                width: "100%",
                                plugins: "link image code autoresize lists table fullpage",
                                toolbar: "formatselect | code | bold italic underline | blocks | bullist numlist | link | table | outdent indent",
                                table_toolbar: "tabledelete | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol",
                                fullpage_default_doctype: '<!DOCTYPE html>',
                                mobile: { toolbar_mode: "wrap" },
                                language: "en",
                                menubar: false,
                                statusbar: false,
                                branding: false,
                                promotion: false,
                                force_br_newlines: true,
                            }}
                        />
                        <div className="flex items-center justify-end mt-16">
                            <Button
                                disabled={submitting}
                                className="ant-btn-primary text-16 weight-400 px-40"
                                onClick={handleSaveTemplate}
                            >
                                Save
                            </Button>
                        </div>
                    </>
            }
            <ConfirmationModal
                showModal={isDeletionConfirmationVisible}
                onClose={() => setIsDeletionConfirmationVisible(false)}
                confirmationText="Yes"
                cancelText={"No"}
                onSubmit={handleDeleteTemplate}
            >
                <p className="text-16 weight-500 mt-20 mb-10">Since you have emptied the body, we'll go back to using template set for entire company.</p>
                <p className="text-16 weight-500 mb-20">Are you sure you want to proceed?</p>
            </ConfirmationModal>
        </Modal>
    )
}

export default EmailTemplate